import {
  zLabDnaStepSummaryResponse,
  zLabWorkbookSummaryResponse,
  type LabDnaStepSummaryResponse,
  type SaveLabDnaStepRequest,
} from '@resistapp/common/api-types';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { getAuthHeaders, resistomapApiClient } from '../../../utils/fetch';
import type { LabWorkbookSummary } from './use-lab-workbook';

async function fetchLabDnaStepSummary(workbookId: string): Promise<LabDnaStepSummaryResponse> {
  const response = await resistomapApiClient.get<LabDnaStepSummaryResponse>(`/lab/${workbookId}/dna-step`, {
    headers: getAuthHeaders('application/json'),
  });
  return zLabDnaStepSummaryResponse.parse(response.data);
}

export async function saveLabDnaStep(
  workbookId: string,
  payload: SaveLabDnaStepRequest,
): Promise<LabDnaStepSummaryResponse> {
  const response = await resistomapApiClient.patch<LabDnaStepSummaryResponse>(`/lab/${workbookId}/dna-step`, payload, {
    headers: getAuthHeaders('application/json'),
  });
  return zLabDnaStepSummaryResponse.parse(response.data);
}

export async function markLabDnaStepReady(workbookId: string): Promise<LabWorkbookSummary> {
  const response = await resistomapApiClient.post(`/lab/${workbookId}/dna-step/ready`, {}, {
    headers: getAuthHeaders('application/json'),
  });
  return zLabWorkbookSummaryResponse.parse(response.data) as LabWorkbookSummary;
}

export function useLabDnaStep(
  workbookId: string | null,
  options?: Omit<UseQueryOptions<LabDnaStepSummaryResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LabDnaStepSummaryResponse>({
    queryKey: ['labDnaStep', workbookId],
    queryFn: async () => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      return await fetchLabDnaStepSummary(workbookId);
    },
    enabled: Boolean(workbookId),
    staleTime: 30_000,
    ...options,
  });
}
